import { inject } from "mobx-react";

import { ClickSection, socialConfigAbstract, Store } from "@inferno/renderer-shared-core";
import type { Site } from "@ihr-radioedit/inferno-core";
import { SitesMenu } from "@ihr-radioedit/inferno-webapi";
import { reverseRoute } from "@inferno/renderer-shared-core";

import { SocialIcons } from "../icons/SocialIcons.component";
import "./CoastFooter.style.scss";
import { Container } from "../elements/Container.component";
import MagicLink from "../elements/MagicLink.component";

interface FooterProps {
  store?: Store;
  amp?: boolean;
}

interface ResolvedMenu extends SitesMenu {
  href?: string;
}

const resolveFooterLinks = (menu: ResolvedMenu, site: Site): ResolvedMenu => {
  if (!menu.href && menu.ref) {
    menu.href = menu.type === "page" ? reverseRoute(site, menu.ref) : menu.ref;
  }

  if (menu.children) {
    menu.children = menu.children.map(m => m && resolveFooterLinks(m, site));
  }

  return menu;
};

const resolveFooterItems = (site: Site, localFooter?: SitesMenu[] | null) => {
  if (!localFooter) {
    return null;
  }

  return localFooter.map(menu => resolveFooterLinks(menu, site));
};

const getLabel = (item: any) => (typeof item.label === "string" ? item.label : "");
const isCAVersion = (label: string) => label === "Privacy Policy CA" || label === "Terms of Use CA";

export const CoastFooter = inject("store")(({ store, amp }: FooterProps) => {
  const countryCode = (store?.request?.countryCode || "").toUpperCase();

  if (!store) {
    return null;
  }

  const { site, env } = store;
  const { sections } = site;

  const socialIcons = socialConfigAbstract(sections.social);

  const resolvedMenuItems = resolveFooterItems(site, sections.navigation?.menu_footer);

  const handlePrivacyChoicesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    store.privacy.toggleModal();
  };

  const isCanada = countryCode === "CA";

  const filteredMenuItems = resolvedMenuItems
    ? resolvedMenuItems.reduce<ResolvedMenu[]>((acc, menuItem) => {
        const label = getLabel(menuItem);

        if (
          (isCanada && label.includes("Privacy Policy") && label !== "Privacy Policy CA") ||
          (isCanada && label.includes("Terms of Use") && label !== "Terms of Use CA") ||
          (!isCanada && isCAVersion(label))
        ) {
          return acc;
        }

        acc.push(menuItem);
        return acc;
      }, [])
    : null;

  const menuItems = filteredMenuItems
    ? filteredMenuItems.map((menuItem, index: number) => {
        let label = getLabel(menuItem);
        const safeHref = typeof menuItem.href === "string" ? menuItem.href : "";
        const safeRef = typeof menuItem.ref === "string" ? menuItem.ref : "";

        if (label === "Privacy Policy CA") {
          label = "Privacy Policy";
        } else if (label === "Terms of Use CA") {
          label = "Terms of Use";
        }

        if (label === "Your Privacy Choices" && !store.privacy.enabled) {
          return null;
        }

        return (
          <li key={index}>
            {label === "Your Privacy Choices" ? (
              <MagicLink to="#" onClick={handlePrivacyChoicesClick} target={menuItem.target || ""}>
                {label}
                {env.YOURCHOICE_ICON && !amp ? (
                  <>
                    &nbsp;
                    <img data-src={env.YOURCHOICE_ICON} alt="Your privacy Logo" className="lazyload" />
                  </>
                ) : null}
              </MagicLink>
            ) : (
              <MagicLink key={index} to={safeHref || safeRef || ""} target={menuItem.target || ""}>
                {label}
              </MagicLink>
            )}
          </li>
        );
      })
    : null;

  return (
    <ClickSection.Provider value="footer">
      <footer className="component-coast-footer">
        <Container block={false}>
          <div className="component-social-network-icons">
            <SocialIcons networks={socialIcons} />
          </div>

          <div className="site-ownership">
            Coast to Coast AM -&nbsp;
            <a href="http://www.premierenetworks.com/" target="_blank" rel="noreferrer">
              &copy;{new Date().getFullYear()} Premiere Networks Inc.
            </a>
          </div>

          <div className="footer-row footer-links">
            {sections.navigation?.menu_footer?.length ? (
              <ul>
                {menuItems}
                <li>
                  <MagicLink to={`/adchoices/`}>
                    AdChoices
                    {env.AD_ICON && !amp ? (
                      <>
                        &nbsp;
                        <img data-src={env.AD_ICON} alt="AdChoices Logo" className="lazyload" />
                      </>
                    ) : null}
                  </MagicLink>
                </li>
              </ul>
            ) : null}
          </div>
        </Container>
      </footer>
    </ClickSection.Provider>
  );
});
